import { formatMoney } from '@betterment/js-runtime';
import { Box, Icon, ListRow, Text } from '@betterment/samba-react';

import { LeadingIcon } from './LeadingIcon';
import { LegacyCheckingAccountRow } from './LegacyCheckingAccountRow';

import { useColorMode } from '../../../Shared/components-v2/color-mode/color-mode-state';
import { StyledLinkRow, titleCss } from '../../../Shared/components-v2/styled-link-row/StyledLinkRow';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { EnvelopesCardQuery } from '../graphql/query.envelopesCard.generated';

type Envelope = EnvelopesCardQuery['envelopes'][number];
type EnvelopeAccount = Envelope['accounts'][number];

interface AccountRowProps {
  account: EnvelopeAccount;
  hasRecurringTransaction: boolean;
}

export const AccountRow: React.FC<AccountRowProps> = ({ account, hasRecurringTransaction }) => {
  const { colorMode } = useColorMode();
  if ('remote' in account) return <LegacyCheckingAccountRow account={account} />;

  const balance = getEnvelopeAccountBalance(account);
  const name = getEnvelopeAccountName(account);

  if (!balance || !name) return null;

  return (
    <StyledLinkRow to={urlBuilder.appAccount.absolute({ accountId: account.id })} name={name}>
      <ListRow.LeadingAsset>
        <LeadingIcon url={account.icon[colorMode]} />
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text color="text" css={titleCss}>
          {name}
        </Text>
        <Text color="text.weak" variant="text50">
          {getRowSubtext(account)}
        </Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingContent>
        <Box css={{ display: 'flex', alignItems: 'center', height: '100%', gap: 'space.100' }}>
          {hasRecurringTransaction && <Icon name="ArrowCircle" color="icon.success" size="xsmall" />}
          <Text color="text">{formatMoney(balance)}</Text>
        </Box>
      </ListRow.TrailingContent>
    </StyledLinkRow>
  );
};

function getEnvelopeAccountBalance(account: EnvelopeAccount) {
  switch (account.__typename) {
    case 'CheckingAccount':
      return account.remote?.balance;
    case 'CashAccount':
    case 'DirectTradingAccount':
      return account.balance;
    case 'ManagedAccount':
      return account.balance;
    case 'Five29Account':
      return account.connection?.balance;
    case 'ExternalAccountEnvelopeAssociation':
      return account.account.balance;
    default:
      return null;
  }
}

function getEnvelopeAccountName(account: EnvelopeAccount) {
  switch (account.__typename) {
    case 'CheckingAccount':
      return account.remote?.name || 'Checking';
    case 'CashAccount':
    case 'DirectTradingAccount':
      return account.name;
    case 'ManagedAccount':
      return account.name;
    case 'Five29Account':
      return account.connection?.name || '529 Plan';
    case 'ExternalAccountEnvelopeAssociation':
      return account.account.name;
    default:
      return null;
  }
}

function getRowSubtext(account: EnvelopeAccount) {
  switch (account.__typename) {
    case 'CashAccount':
      return `${account.apy.rate}% APY*`;
    case 'ManagedAccount':
      return account.portfolio?.configurationDescription;
    default:
      return undefined;
  }
}
